import { FC, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import { enqueueSnackbar } from 'notistack';

import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Icons,
    MuiLoadingButton,
    Theme,
    Typography,
} from '@thirdspacelearning/library';

import { useBadges } from '../../hooks';
import { BadgeStatus, IBadge } from '../../types';
import { currentProgrammeSelector } from '@store/programme';

import { postBadge } from '@api';
import { StudentQueryKeys } from '@services';

const sx = {
    badgeIcon: {
        fontSize: '4.5rem',
    },
    iconButton: { p: 0, borderRadius: '50%', overflow: 'hidden' },
    badgeIconAvailable: (theme: Theme) => ({
        boxShadow: `0px 0px 15px 0px ${theme.palette.primary.main}`,
    }),
    dialog: {
        '& .MuiPaper-root': {
            overflow: 'visible',
            display: 'flex',
            flexDirection: 'row',
            minHeight: '295px',
            gap: 4,
            p: 4,
        },
    },
    dialogBadge: {
        width: '100%',
        height: '100%',
        minWidth: '200px',
        maxHeight: '280px',
    },
    dialogContent: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'center',
        flexBasis: '75%',
        gap: 2,
        p: 0,
    },
    closeButton: (theme: Theme) => ({
        position: 'absolute',
        right: '-8px',
        top: '-8px',
        width: '50px',
        height: '50px',
        color: 'grey.500',
        bgcolor: 'common.white',
        borderRadius: '50%',
        fontSize: '0.875rem',
        boxShadow: theme.shadows[2],
        '&:hover, &.Mui-focusVisible': { backgroundColor: 'common.white' },
    }),
    closeIcon: { fontSize: '2rem', color: 'grey.600' },
    lockIcon: { mr: 1 },
};

type Props = {
    badge: IBadge;
};

const Badge: FC<Props> = ({ badge }) => {
    const queryClient = useQueryClient();

    const lottieRef = useRef<LottieRefCurrentProps | null>(null);

    const currentProgramme = useSelector(currentProgrammeSelector);

    const [open, setOpen] = useState(false);

    const { claimed } = badge;

    const {
        title,
        description,
        badgeStatus,
        BadgeComponent,
        DialogBadgeComponent,
        dialogLottieAnimation,
        buttonData,
    } = useBadges(badge);

    const {
        mutate: postBadgeClaim,
        isPending,
        isSuccess,
    } = useMutation({
        mutationFn: (code: string) => postBadge(code, currentProgramme?.id),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: [StudentQueryKeys.BADGES],
            });
        },
        onError: (error: Error) => {
            enqueueSnackbar(error.message, {
                variant: 'sxSnackbar',
                severity: 'error',
                testId: 'badge-claim-error',
            });
        },
    });

    const onClaim = () => {
        postBadgeClaim(badge.code);
    };

    const onClose = () => {
        setOpen(false);
    };

    return (
        <Box>
            <IconButton
                sx={[
                    sx.iconButton,
                    badgeStatus === BadgeStatus.BADGE_AVAILABLE &&
                        sx.badgeIconAvailable,
                ]}
                onClick={() => setOpen(true)}
                aria-label="Programme badge"
                data-testid={`badge`}
            >
                <BadgeComponent sx={sx.badgeIcon} />
            </IconButton>

            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="sm"
                sx={sx.dialog}
                data-testid="rewardDialog"
            >
                <IconButton
                    aria-label="Close reward dialog"
                    onClick={onClose}
                    sx={sx.closeButton}
                    data-testid="dialog-close-button"
                >
                    <Icons.Close sx={sx.closeIcon} />
                </IconButton>

                <Box
                    display="flex"
                    flexBasis="45%"
                    alignItems="center"
                    justifyContent="center"
                >
                    {badgeStatus === BadgeStatus.BADGE_CLAIMED ? (
                        <Lottie
                            lottieRef={lottieRef}
                            animationData={dialogLottieAnimation}
                            loop={false}
                            autoplay={true}
                        />
                    ) : (
                        <DialogBadgeComponent sx={sx.dialogBadge} />
                    )}
                </Box>

                <DialogContent sx={sx.dialogContent} data-testid="badge-dialog">
                    <Typography
                        variant="h5"
                        fontWeight="bold"
                        data-testid="dialog-title"
                    >
                        {title}
                    </Typography>
                    <Typography data-testid="dialog-description">
                        {description}
                    </Typography>
                    {!claimed && (
                        <MuiLoadingButton
                            color="primary"
                            variant="contained"
                            onClick={buttonData.buttonAction || onClaim}
                            // isSuccess here to avoid showing the button in between states
                            loading={isPending || isSuccess}
                            disabled={buttonData.lockedButton}
                            loadingIndicator="Claiming..."
                            data-testid="dialog-button"
                        >
                            {buttonData.lockedButton && (
                                <Icons.Lock sx={sx.lockIcon} />
                            )}
                            {buttonData.buttonText}
                        </MuiLoadingButton>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Badge;
